import { useEffect, useState } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { areaByActiveBySortKey } from "../../../graphql/reloadyard/custom-queries";
import { filterZones } from "../../../utils/locationUtils";
import { useWorkOrderContext } from "./context";
import { InventoryStatus } from "../../../utils/constants";

// Reusable hook to manage zone selection across aisle controls.
// Selector should persist after selection and allow changing zone.
export const useZoneSelector = () => {
  const { state, dispatch, getAisles } = useWorkOrderContext();
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const currentInventory = state?.currentInventory || {};

  // Zone selector visibility rules:
  // - Visible if account does NOT require default zone OR no zone chosen yet
  // - AND either no items have been placed yet, or we're editing the only item
  const items = currentInventory?.inventoryItems?.items || [];
  const hasPlaced = items.some(
    (itm) => itm?.status && itm.status !== InventoryStatus.IN_TRANSIT,
  );
  const singleItemEdit =
    items.length === 1 && items[0]?.id === state?.selectedInventoryItem?.id;
  const zoneId = !!currentInventory?.zoneId?.trim();
  const accountAllows =
    state?.currentShipment?.account?.requireDefaultZone !== true || !zoneId;
  const allowedByPlacement = singleItemEdit || !hasPlaced;
  const showZoneSelect =
    !!currentInventory?.areaId && accountAllows && allowedByPlacement;

  useEffect(() => {
    const loadZones = async () => {
      if (!currentInventory?.areaId) return;
      let areas = window.appUser?.reloadYard?.areas?.items;
      if (!areas || areas.length === 0) {
        const resp = await API.graphql(
          graphqlOperation(areaByActiveBySortKey, {
            reloadYardId: window.appUser?.reloadYardId,
            activeSortKey: { beginsWith: { active: "true" } },
            limit: 1000,
          }),
        );
        areas = resp.data.areaByActiveBySortKey.items;
        if (window?.appUser?.reloadYard) {
          window.appUser.reloadYard.areas = { items: areas };
        }
      }
      const area = areas.find((a) => a.id === currentInventory.areaId);
      const filtered = area?.zones?.items
        ? filterZones(
            area.zones.items,
            currentInventory.reloadYardCommodityId,
            currentInventory.accountId,
          )
        : [];
      setZones(filtered);
      // If there is already a selected zoneId, sync selectedZone from list
      if (zoneId) {
        const z = filtered.find((z) => z.id === currentInventory.zoneId);
        setSelectedZone(z || null);
      } else if (filtered.length === 1) {
        // Auto-select only when none is set
        handleZoneChange(filtered[0].id, filtered);
      } else {
        setSelectedZone(null);
      }
    };
    loadZones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentInventory?.areaId, currentInventory?.zoneId]);

  const handleZoneChange = (zoneId, zoneChoices = zones) => {
    if (!zoneId) return;
    const zone = zoneChoices?.find((z) => z.id === zoneId);
    if (!zone) return;
    setSelectedZone(zone);
    // Clear current aisle and location selections when zone changes
    dispatch({ type: "SET_SELECTED_LOCATION", payload: null });
    dispatch({
      type: "SET_SELECTED_AISLE",
      payload: {
        aisle: null,
        bayList: [],
        showCapacityDisplay: false,
        selectedLocation: null,
        selectedInventoryItem: state.selectedInventoryItem,
      },
    });
    const updatedInventory = {
      ...currentInventory,
      zoneId: zone.id,
      zone: zone,
    };
    dispatch({ type: "UPDATE_CURRENT_INVENTORY", payload: updatedInventory });
    getAisles(updatedInventory);
  };

  return { zones, selectedZone, showZoneSelect, handleZoneChange };
};
